import { useState, useEffect, useCallback, useRef } from 'react';

interface Message {
    id: string | number;
    role: string;
    [key: string]: any;
}

interface UseVirtualizedMessagesProps {
    messages: Message[];
    windowSize?: number;
    chatHistoryRef: React.RefObject<HTMLDivElement | null>;
}

interface VirtualizedMessagesResult {
    visibleMessages: Message[];
    startIndex: number;
    endIndex: number;
    topPadding: number;
    bottomPadding: number;
    handleScroll: () => void;
    scrollToBottom: () => void;
    isAtBottom: boolean;
}

const AVERAGE_MESSAGE_HEIGHT = 150; // Approximate height in pixels
const SCROLL_THRESHOLD = 200; // Distance from top to trigger loading more messages

export const useVirtualizedMessages = ({
    messages,
    windowSize = 30,
    chatHistoryRef
}: UseVirtualizedMessagesProps): VirtualizedMessagesResult => {
    const [startIndex, setStartIndex] = useState(0);
    const [endIndex, setEndIndex] = useState(windowSize);
    const [isAtBottom, setIsAtBottom] = useState(true);
    const scrollPositionRef = useRef<number>(0);
    const isScrollingProgrammatically = useRef(false);
    const previousMessagesLength = useRef(messages.length);

    const scrollToBottom = useCallback(() => {
        if (!chatHistoryRef.current) return;
        
        isScrollingProgrammatically.current = true;
        
        // Ensure we're showing the last messages
        const totalMessages = messages.length;
        if (totalMessages > 0) {
            const newStartIndex = Math.max(0, totalMessages - windowSize);
            setStartIndex(newStartIndex);
            setEndIndex(totalMessages);
        }
        
        setTimeout(() => {
            if (chatHistoryRef.current) {
                chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
                setIsAtBottom(true);
            }
            
            // Reset flag after scroll completes
            setTimeout(() => {
                isScrollingProgrammatically.current = false;
            }, 100);
        }, 50);
    }, [messages.length, windowSize, chatHistoryRef]);

    // Initialize to show last messages
    useEffect(() => {
        const totalMessages = messages.length;
        if (totalMessages > 0) {
            const newStartIndex = Math.max(0, totalMessages - windowSize);
            setStartIndex(newStartIndex);
            setEndIndex(totalMessages);
        }
    }, [messages.length, windowSize]);

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        const totalMessages = messages.length;
        const hasNewMessages = totalMessages > previousMessagesLength.current;
        
        if (hasNewMessages) {
            if (isAtBottom) {
                // New message added and user is at bottom, stay at bottom
                const newStartIndex = Math.max(0, totalMessages - windowSize);
                setStartIndex(newStartIndex);
                setEndIndex(totalMessages);
                
                // Scroll to bottom after state update
                setTimeout(() => {
                    scrollToBottom();
                }, 50);
            } else {
                // New message added but user is not at bottom, just update indices
                const newStartIndex = Math.max(0, totalMessages - windowSize);
                setStartIndex(newStartIndex);
                setEndIndex(totalMessages);
            }
        }
        
        previousMessagesLength.current = totalMessages;
    }, [messages.length, isAtBottom, windowSize, scrollToBottom]);

    // Ensure visible messages are always up to date when messages array changes
    useEffect(() => {
        const totalMessages = messages.length;
        if (totalMessages > 0) {
            // If we're showing messages but the endIndex is less than total messages,
            // update to show the latest messages
            if (endIndex < totalMessages) {
                const newStartIndex = Math.max(0, totalMessages - windowSize);
                setStartIndex(newStartIndex);
                setEndIndex(totalMessages);
            }
        }
    }, [messages.length, endIndex, windowSize]);

    const handleScroll = useCallback(() => {
        if (!chatHistoryRef.current || isScrollingProgrammatically.current) return;

        const { scrollTop, scrollHeight, clientHeight } = chatHistoryRef.current;
        const totalMessages = messages.length;
        
        // Check if at bottom
        const atBottom = scrollHeight - scrollTop - clientHeight < 50;
        setIsAtBottom(atBottom);

        // Store scroll position
        scrollPositionRef.current = scrollTop;

        // Load more messages at the top
        if (scrollTop < SCROLL_THRESHOLD && startIndex > 0) {
            const messagesToLoad = Math.min(10, startIndex);
            const newStartIndex = startIndex - messagesToLoad;
            
            // Calculate current scroll position in content
            const currentScrollFromTop = scrollTop;
            
            setStartIndex(newStartIndex);
            
            // Maintain scroll position after prepending messages
            setTimeout(() => {
                if (chatHistoryRef.current) {
                    const addedHeight = messagesToLoad * AVERAGE_MESSAGE_HEIGHT;
                    chatHistoryRef.current.scrollTop = currentScrollFromTop + addedHeight;
                }
            }, 0);
        }

        // Remove messages from bottom when scrolling up
        if (!atBottom && endIndex === totalMessages && totalMessages > windowSize) {
            const visibleMessages = endIndex - startIndex;
            if (visibleMessages > windowSize + 10) {
                setEndIndex(startIndex + windowSize);
            }
        }

        // Load more messages at the bottom when scrolling down
        if (atBottom && endIndex < totalMessages) {
            const messagesToLoad = Math.min(10, totalMessages - endIndex);
            setEndIndex(endIndex + messagesToLoad);
        }

        // Remove messages from top when at bottom
        if (atBottom && endIndex - startIndex > windowSize) {
            const newStartIndex = Math.max(0, endIndex - windowSize);
            setStartIndex(newStartIndex);
        }
    }, [messages.length, startIndex, endIndex, windowSize, chatHistoryRef]);

    // Calculate padding for messages outside the visible window
    const topPadding = startIndex * AVERAGE_MESSAGE_HEIGHT;
    const bottomPadding = Math.max(0, (messages.length - endIndex) * AVERAGE_MESSAGE_HEIGHT);

    const visibleMessages = messages.slice(startIndex, endIndex);

    return {
        visibleMessages,
        startIndex,
        endIndex,
        topPadding,
        bottomPadding,
        handleScroll,
        scrollToBottom,
        isAtBottom
    };
};
